import { ApolloCache, gql, useMutation, useQuery } from '@provision/graphql-client';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isNullish } from 'remeda';
import { Records } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import { useCurrentUserProfile } from './platform-user';

export const GET_CONVERSATION_AGGREGATIONS_QUERY = gql`
  query GetConversationAggregations($input: GetConversationAggregationsInput!) {
    getConversationAggregations(input: $input) {
      records {
        id
        conversationId
        unseenCount
        lastMessageCreatedAt
      }
    }
  }
`;

export interface ConversationAggregationsRecord {
  conversationId: string;
  unseenCount: number;
  lastMessageCreatedAt: string;
}

export interface GetConversationAggregationsQuery {
  getConversationAggregations: Records<ConversationAggregationsRecord>;
}

export const useConversationAggregations = ({
  conversationIds,
}: {
  conversationIds: string[];
}) => {
  const { user } = useCurrentUserProfile();

  // keep track of the last ids we actually fetched against
  const lastFetchedIdsRef = useRef<string[] | null>(null);

  // sorting to produce a stable reference for apollo
  const sortedIds = useMemo(() => [...conversationIds].sort(), [conversationIds]);

  // Determine if the change in conversation IDs is an append-only update (e.g., one new incoming ID)
  // This allows us to skip unnecessary refetching when the update isn't a meaningful change (like a full refresh or reordering).

  const isAppendOnly = useMemo(() => {
    const prev = lastFetchedIdsRef.current;

    // no prior fetch means we *should* fetch
    if (!prev) {
      return false;
    }

    const added = sortedIds.filter((id) => !prev.includes(id));
    const removed = prev.filter((id) => !sortedIds.includes(id));

    // simple diff check if conversation ids were incremented only
    return added.length === 1 && (removed.length === 0 || removed.length === 1);
  }, [sortedIds]);

  const skip =
    !user?.id ||
    sortedIds.length === 0 ||
    (lastFetchedIdsRef.current !== null && isAppendOnly);

  const {
    data: rawData,
    previousData,
    refetch,
    client,
    ...rest
  } = useQuery<GetConversationAggregationsQuery>(GET_CONVERSATION_AGGREGATIONS_QUERY, {
    variables: { input: { userId: user?.id, conversationIds: sortedIds } },
    skip,
  });

  useEffect(() => {
    if (!skip) {
      lastFetchedIdsRef.current = sortedIds;
    }
  }, [skip, sortedIds]);

  // this avoids UI jumping on downloading new bunch of conversations
  const data = rawData || previousData;

  const conversationAggregationsMap = useMemo(
    () =>
      new Map(data?.getConversationAggregations?.records.map((i) => [i.conversationId, i])),
    [data?.getConversationAggregations?.records],
  );

  return {
    data,
    conversationAggregationsMap,
    ...rest,
  };
};

const UPDATE_CONVERSATION_AS_SEEN = gql`
  mutation UpdateConversationAsSeen($input: UpdateConversationAsSeenInput!) {
    updateConversationAsSeen(input: $input) {
      success
    }
  }
`;

export const useMarkMessagesAsSeen = (initialConversationId?: string) => {
  const { showErrorAlert } = useNotifications();

  const [updateConversationAsSeen, { loading, client }] = useMutation(
    UPDATE_CONVERSATION_AS_SEEN,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong on marking conversation as seen');
      },
    },
  );

  const markAsSeenIfNeeded = useCallback(
    async (newConversationId?: string) => {
      const conversationId = newConversationId ?? initialConversationId;
      if (!conversationId) {
        console.error(
          `Error, tried to mark conversation as seen with invalid arguments conversationId: ${conversationId}`,
        );
        return;
      }

      const aggregation = getCachedAggregation(client.cache, conversationId);

      // return early if the latest cache item does does contain the count
      if (!aggregation?.unseenCount) {
        return;
      }

      // optimistically reset the count in the cache
      modifyConversationAggregationsCacheItem(client.cache, conversationId, {
        unseenCount: 0,
      });

      // perform real api call to mark as seen
      await updateConversationAsSeen({
        variables: {
          input: {
            conversationId,
          },
        },
      });
    },
    [initialConversationId, client, updateConversationAsSeen],
  );

  return { markAsSeenIfNeeded, loading };
};

export function getCachedAggregation(cache: ApolloCache<unknown>, conversationId?: string) {
  if (!conversationId) {
    return;
  }

  const fragment = gql`
    fragment CurrentAggregation on GetConversationAggregationsRecord {
      id
      conversationId
      unseenCount
    }
  `;

  const id = cache.identify({
    __typename: 'GetConversationAggregationsRecord',
    id: conversationId,
  });

  return cache.readFragment<ConversationAggregationsRecord>({
    id,
    fragment,
    fragmentName: 'CurrentAggregation',
  });
}

export function modifyConversationAggregationsCacheItem(
  cache: ApolloCache<unknown>,
  conversationId: string,
  patch: {
    lastMessageCreatedAt?: string;
    unseenCount?: number;
    unseenCountDiff?: number;
  },
) {
  const id = cache.identify({
    __typename: 'GetConversationAggregationsRecord',
    id: conversationId,
  });

  if (!id) {
    return;
  }

  cache.modify({
    id,
    fields: {
      unseenCount(existingCount: number) {
        // if a diff is provided (even if zero), adjust the count.
        if (!isNullish(patch.unseenCountDiff)) {
          return Math.max(0, existingCount + patch.unseenCountDiff);
        }

        // otherwise, if a new absolute count is provided, use it.
        if (!isNullish(patch.unseenCount)) {
          return patch.unseenCount;
        }

        return existingCount;
      },
      // update lastMessageCreatedAt only if the patch provides a new value.
      lastMessageCreatedAt(existingValue: string) {
        return patch.lastMessageCreatedAt ?? existingValue;
      },
    },
  });
}
